import { useQuery } from '@tanstack/react-query';

import { AnyDocument } from '../../../../types';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { NonPaginatedResponse } from '../../../types/NonPaginatedResponse';
import { getDocumentSelectorQueryKey } from '../../logic/getDocumentSelectorQueryKey';
import { getDocumentSelector } from '../../requests/getDocumentSelector';
import { DocumentParams } from '../../types/DocumentParams';

export const useDocumentSelector = <
  T,
  DocumentType extends AnyDocument = AnyDocument,
>(
  documentParams: DocumentParams,
  documentId: DocumentType['id'],
  selector: string | undefined,
) => {
  const { constructGet } = useRequestConstructor();

  const queryKey = getDocumentSelectorQueryKey<DocumentType>(
    documentId,
    documentParams,
    selector,
  );

  const { data, refetch, ...rest } = useQuery<
    NonPaginatedResponse<T> | undefined
  >({
    queryKey,
    queryFn: async () =>
      getDocumentSelector<T>(
        constructGet,
        documentParams,
        documentId,
        selector!,
      ),
    enabled: Boolean(documentId) && Boolean(selector),
    keepPreviousData: true,
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(data, refetch, 'selector'),
    ...rest,
  };
};
